import { Link } from 'react-router-dom'
import {
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline'

const Footer = () => {
  const currentYear = new Date().getFullYear()
  const contactEmail = import.meta.env.VITE_CONTACT_EMAIL || 'kontakt@cryptedmoon.com'
  const contactPhone = import.meta.env.VITE_CONTACT_PHONE || '+49 8709 6489972'
  const linkedinUrl = import.meta.env.VITE_LINKEDIN_URL || 'https://www.linkedin.com/company/cryptedmoon'
  const trustpilotUrl = import.meta.env.VITE_TRUSTPILOT_URL || 'https://www.trustpilot.com/review/cryptedmoon.com'
  const instagramUrl = import.meta.env.VITE_INSTAGRAM_URL || 'https://www.instagram.com/cryptedmoon'

  const navigation = {
    services: [
      { name: 'Webdesign & Entwicklung', href: '/services/web' },
      { name: 'E-Commerce & Shopify', href: '/services/ecommerce' },
      { name: 'Suchmaschinenoptimierung (SEO)', href: '/services/seo' },
      { name: 'Domains & Hosting', href: '/services/domains' },
      { name: 'E-Mail Security', href: '/services/email-security' },
      { name: 'Digital Signage', href: '/services/digital-signage' },
      { name: 'Print Design', href: '/services/print-design' }
    ],
    company: [
      { name: 'Über uns', href: '/about' },
      { name: 'Blog', href: '/blog' },
      { name: 'Portfolio', href: '/portfolio' },
      { name: 'Kontakt', href: '/contact' },
      { name: 'Projekt-Check', href: '/termin' }
    ],
    legal: [
      { name: 'Impressum', href: '/impressum' },
      { name: 'Datenschutz', href: '/datenschutz' },
      { name: 'AGB', href: '/agb' },
      { name: 'Cookie-Einstellungen', href: '#', onClick: () => {
        document.cookie = 'cookie-consent=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;'
        window.location.reload()
      }}
    ],
    social: [
      {
        name: 'LinkedIn',
        href: linkedinUrl,
        icon: (props) => (
          <svg fill="currentColor" viewBox="0 0 24 24" {...props}>
            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
          </svg>
        )
      },
      {
        name: 'Trustpilot',
        href: trustpilotUrl,
        icon: (props) => (
          <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
            <path d="M12 2.25 9.42 9.93H2.25l5.79 4.2-2.22 6.62L12 17.52l6.18 3.23-2.22-6.62 5.79-4.2h-7.17L12 2.25Z" />
          </svg>
        )
      },
      {
        name: 'Instagram',
        href: instagramUrl,
        icon: (props) => (
          <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
            <path d="M7 2h10a5 5 0 0 1 5 5v10a5 5 0 0 1-5 5H7a5 5 0 0 1-5-5V7a5 5 0 0 1 5-5Zm0 2a3 3 0 0 0-3 3v10a3 3 0 0 0 3 3h10a3 3 0 0 0 3-3V7a3 3 0 0 0-3-3H7Zm5 3.5A5.5 5.5 0 1 1 6.5 13 5.5 5.5 0 0 1 12 7.5Zm0 2A3.5 3.5 0 1 0 15.5 13 3.5 3.5 0 0 0 12 9.5Zm5.75-3a1.25 1.25 0 1 1-1.25 1.25 1.25 1.25 0 0 1 1.25-1.25Z" />
          </svg>
        )
      }
    ]
  }

  return (
    <footer className="bg-dark-900 text-white" role="contentinfo">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-28 md:pb-12">
        {/* Main Footer Content */}
        <div className="py-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-12">
          {/* Company Info */}
          <div className="lg:col-span-2">
            <Link to="/" className="flex items-center gap-3 mb-5">
              <picture className="block h-8 w-auto">
                <source srcSet="/images/logo/logo-light.webp" media="(prefers-color-scheme: dark)" type="image/webp" />
                <source srcSet="/images/logo/logo-light.png" media="(prefers-color-scheme: dark)" />
                <source srcSet="/images/logo/logo-dark.webp" type="image/webp" />
                <img
                  src="/images/logo/logo-dark.png"
                  alt="cryptedmoon Logo"
                  width="200"
                  height="40"
                  className="h-8 w-auto"
                  loading="lazy"
                />
              </picture>
            </Link>

            <p className="text-sm text-primary-400 font-medium mb-3">
              Digital. Sicher. Erfolgreich.
            </p>

            <p className="text-sm text-dark-400 mb-7 max-w-md leading-relaxed">
              Ihre professionelle Webdesign-Agentur und Hornetsecurity Partner aus Landshut.
              Seit 2021 unterstützen wir Unternehmen aus Niederbayern, Oberbayern und der Oberpfalz digital erfolgreich zu werden.
            </p>

            {/* Contact Info */}
            <div className="space-y-3" data-nosnippet>
              <div className="flex items-center gap-2.5">
                <MapPinIcon className="w-4 h-4 text-dark-500 flex-shrink-0" />
                <span className="text-sm text-dark-400">Landshut, Bayern</span>
              </div>
              <div className="flex items-center gap-2.5">
                <EnvelopeIcon className="w-4 h-4 text-dark-500 flex-shrink-0" />
                <a
                  href={`mailto:${contactEmail}`}
                  className="text-sm text-dark-400 hover:text-white transition-colors duration-150"
                  data-nosnippet
                >
                  {contactEmail}
                </a>
              </div>
              <div className="flex items-center gap-2.5">
                <PhoneIcon className="w-4 h-4 text-dark-500 flex-shrink-0" />
                <a
                  href={`tel:${contactPhone.replace(/\s+/g, '')}`}
                  className="text-sm text-dark-400 hover:text-white transition-colors duration-150"
                  data-nosnippet
                >
                  {contactPhone}
                </a>
              </div>
              <div className="flex items-center gap-2.5">
                <svg className="w-4 h-4 text-dark-500 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
                <a
                  href="https://wa.me/4916098658025"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-dark-400 hover:text-white transition-colors duration-150"
                >
                  WhatsApp
                </a>
              </div>
            </div>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-sm font-semibold text-white mb-4">Leistungen</h3>
            <ul className="space-y-2.5">
              {navigation.services.map((item) => (
                <li key={item.name}>
                  <Link
                    to={item.href}
                    className="text-sm text-dark-400 hover:text-white transition-colors duration-150 flex items-center gap-1.5 group"
                  >
                    <span>{item.name}</span>
                    <ArrowRightIcon className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity duration-150" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h3 className="text-sm font-semibold text-white mb-4">Unternehmen</h3>
            <ul className="space-y-2.5">
              {navigation.company.map((item) => (
                <li key={item.name}>
                  <Link
                    to={item.href}
                    className="text-sm text-dark-400 hover:text-white transition-colors duration-150 flex items-center gap-1.5 group"
                  >
                    <span>{item.name}</span>
                    <ArrowRightIcon className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity duration-150" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="py-6 border-t border-dark-800">
          <div className="flex flex-col md:flex-row justify-between items-center gap-5">
            {/* Copyright */}
            <div className="text-dark-500 text-xs">
              &copy; {currentYear} cryptedmoon. Alle Rechte vorbehalten.
            </div>

            {/* Social Links */}
            <div className="flex items-center gap-4">
              {navigation.social.map((item) => (
                <a
                  key={item.name}
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-dark-500 hover:text-white transition-colors duration-150"
                  aria-label={`Folgen Sie uns auf ${item.name}`}
                >
                  <item.icon className="w-5 h-5" aria-hidden="true" />
                </a>
              ))}
            </div>

            {/* Legal Links */}
            <div className="flex flex-wrap items-center gap-5 text-xs">
              {navigation.legal.map((item) => (
                <div key={item.name}>
                  {item.onClick ? (
                    <button
                      onClick={item.onClick}
                      className="text-dark-500 hover:text-white transition-colors duration-150 cursor-pointer"
                    >
                      {item.name}
                    </button>
                  ) : (
                    <Link
                      to={item.href}
                      className="text-dark-500 hover:text-white transition-colors duration-150"
                    >
                      {item.name}
                    </Link>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default Footer
